import { useState, useEffect, useMemo } from "react";
import useUser from "../hooks/use-user";
import ThreeDotsLoader from "@/components/common/three-dots-loader";
import { Card, CardContent } from "@/components/ui/card";
import { AreaScope, HasAccess } from "@/components/common/has-access";
import VaulDrawer from "@/components/ui/drawer";
import { UpdateUserDetailsDialog } from "./update-user-details-dialog";
import AddEditBundleUsageForm from "./usage-form";
import { UserProfileHeaderRedesigned } from "./user-profile-header-redesigned";
import { CopyReschedulePaymentLinkButton } from "./copy-reschedule-payment-link-button";
import { SendPushDialog } from "./send-push-dialog";
import { UserNotesSection } from "./user-notes-section";
import { UserSummaryCardRedesigned } from "./user-summary-card-redesigned";
import { BundleUsageTable } from "./bundle-usage-table";
import { useUpdateUtilizedSessions } from "../hooks/use-update-utilized-sessions";
import {
  calculateSessionsSummary,
  calculatePaymentSummary,
  findClosestToExpiry,
  calculateExceptionCounts,
  collectUserExceptionLogs,
  collectNoShowsByProduct,
  collectRhReschedules,
} from "../utils/user-calculations";
import { buildProductSummaries } from "../utils/product-summary";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { useCancelSession } from "@/features/sessions/hooks/use-cancel-session";
import { useCreateDocument } from "@/features/sessions/hooks/use-create-document";
import { useCreateNoShow } from "@/features/sessions/hooks/use-create-no-show";
import { useUndoNoShow } from "@/features/sessions/hooks/use-undo-no-show";
import { useUpdateBundleUsageStatus } from "../hooks/use-update-bundle-usage-status";
import { Session } from "@/pages/sessions/types/session";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CANCELLATION_REASONS } from "@/utils/cancellation-reasons";
import { isWomensHealthBundle } from "@/helpers/womens-health-schedule";

const DASHBOARD_BASE_URL =
  (import.meta.env.VITE_DASHBOARD_URL as string) ??
  "https://dashboard.rockethealth.app";

interface UserDetailsDrawerProps {
  userId: string | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserDetailsDrawer({
  userId,
  isOpen,
  onOpenChange,
}: UserDetailsDrawerProps) {
  const [showBundleUsageFormDrawer, setShowBundleUsageFormDrawer] = useState(false);
  const [showUpdateUserDetailsDialog, setShowUpdateUserDetailsDialog] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedCancelReason, setSelectedCancelReason] = useState<string>("");
  const [cancelReason, setCancelReason] = useState<string | undefined>(undefined);
  const [sessionToCancel, setSessionToCancel] = useState<{ session: Session; bundleUsage: any } | null>(null);
  // Set when the care team clicks the partner pill — swaps the drawer to the
  // partner's profile without navigating away from the underlying page.
  const [overrideUserId, setOverrideUserId] = useState<string | null>(null);
  const effectiveUserId = overrideUserId ?? userId;

  const { data: user, isLoading, error, isFetching } = useUser(effectiveUserId || "", isOpen);
  const { mutate: updateUtilized } = useUpdateUtilizedSessions();
  const { mutate: updateBundleStatus } = useUpdateBundleUsageStatus();
  const { mutate: cancelSession } = useCancelSession();
  const { mutate: createDocument } = useCreateDocument();
  const { mutate: createNoShow } = useCreateNoShow();
  const { mutate: undoNoShow } = useUndoNoShow();
  const { toast } = useToast();

  useEffect(() => {
    if (!isOpen) {
      setShowBundleUsageFormDrawer(false);
      setShowUpdateUserDetailsDialog(false);
      setOverrideUserId(null);
    }
  }, [isOpen]);

  // A new user picked from the parent list resets any partner override.
  useEffect(() => {
    setOverrideUserId(null);
  }, [userId]);

  const handleClose = () => {
    onOpenChange(false);
    setShowBundleUsageFormDrawer(false);
    setShowUpdateUserDetailsDialog(false);
    setOverrideUserId(null);
  };

  // All summary derivations iterate every session of every pack, so they're
  // computed once per user payload — not on every drawer re-render (dialog
  // toggles, cancel flow, toasts). Stable references also let the summary
  // card's own re-renders stay limited to its pager state.
  const summaryData = useMemo(() => {
    if (!user) return null;
    const exceptionLogs = collectUserExceptionLogs(
      user.exceptionLogs,
      user.bundles_usage,
    );
    return {
      sessionsSummary: calculateSessionsSummary(user.bundles_usage),
      paymentSummary: calculatePaymentSummary(user.bundles_usage),
      closestToExpiry: findClosestToExpiry(user.bundles_usage),
      productSummaries: buildProductSummaries(user.bundles_usage),
      exceptionCounts: calculateExceptionCounts(exceptionLogs),
      rhReschedules: collectRhReschedules(user.bundles_usage),
      noShows: collectNoShowsByProduct(user.bundles_usage),
    };
  }, [user]);

  if (!effectiveUserId) {
    return null;
  }

  // Judged as users wherever this drawer is opened from — see AreaScope.
  return (
    <AreaScope area="users">
    <>
      <VaulDrawer
        drawerDirection="right"
        isOpen={isOpen}
        onOpenChange={handleClose}
        triggerChildren={null}
        customClass="w-[620px] md:w-[960px] lg:w-[1100px] h-full bg-black"
        contentChildren={
          <div className="h-[calc(100vh-2rem)] w-full overflow-x-hidden">
            <div className="w-full py-4 space-y-4 px-6 min-w-0 max-w-full overflow-x-hidden">
              {error ? (
                <div className="text-center py-8 text-red-600">
                  Failed to load user details
                </div>
              ) : isFetching || isLoading || !user ? (
                <div className="flex justify-center items-center w-full min-h-[400px] py-8">
                  <ThreeDotsLoader />
                </div>
              ) : (
                <>
                  {(() => {
                    // Structural guard: the memo returns null only when user
                    // is null, which the enclosing ternary already excludes —
                    // but don't let a future edit to that ternary turn this
                    // into a destructure-of-null.
                    if (!summaryData) return null;
                    const {
                      sessionsSummary,
                      paymentSummary,
                      closestToExpiry,
                      productSummaries,
                      exceptionCounts,
                      rhReschedules,
                      noShows,
                    } = summaryData;

                    return (
                      <>
                        <Card className="bg-white border border-gray-200 rounded-lg shadow-sm w-full overflow-hidden">
                          <CardContent className="p-6 overflow-x-hidden">
                            <div className="space-y-6">
                              <UserProfileHeaderRedesigned
                                user={user}
                                onEditClick={() => setShowUpdateUserDetailsDialog(true)}
                                onPartnerClick={setOverrideUserId}
                              />
                              <div className="flex flex-wrap items-center gap-2">
                                <CopyReschedulePaymentLinkButton
                                  user={user}
                                  line="therapy"
                                />
                                <CopyReschedulePaymentLinkButton
                                  user={user}
                                  line="clinical"
                                />
                                <CopyReschedulePaymentLinkButton
                                  user={user}
                                  line="psychiatry"
                                />
                                <SendPushDialog
                                  userId={user.id}
                                  userName={user.full_name}
                                />
                              </div>
                              <UserNotesSection
                                userId={user.id}
                                initialNote={(user as any).notes?.[0]?.content || ""}
                                lastUpdated={(user as any).notes?.[0]?.updated_at}
                                lastUpdatedBy={(user as any).notes?.[0]?.created_by}
                              />
                            </div>
                          </CardContent>
                        </Card>

                        <div className="space-y-2">
                          <h2 className="text-lg font-semibold">Summary</h2>
                          <Card className="bg-orange-500 text-white w-full overflow-hidden">
                            {/* Tighter than the default p-6: the orange tiles
                                carry their own internal padding, so a slim p-2
                                frame keeps the Summary card short. */}
                            <CardContent className="p-2 overflow-x-hidden">
                              <UserSummaryCardRedesigned
                                productSummaries={productSummaries}
                                sessionsSummary={sessionsSummary}
                                paymentSummary={paymentSummary}
                                closestToExpiry={closestToExpiry}
                                exceptionCounts={exceptionCounts}
                                noShows={noShows}
                                rhReschedules={rhReschedules}
                              />
                            </CardContent>
                          </Card>
                        </div>

                        <div className="pb-24">
                          <BundleUsageTable
                            bundles={user.bundles_usage}
                            userId={user.id}
                            userCountry={user.country}
                            onCreatePackClick={() => setShowBundleUsageFormDrawer(true)}
                            onUpdateUtilized={(params: any) => {
                              updateUtilized(params, {
                                onSuccess: () => {
                                  toast({
                                    title: "Utilized sessions updated successfully",
                                  });
                                },
                                onError: () => {
                                  toast({
                                    title: "Failed to update utilized sessions",
                                    variant: "destructive",
                                  });
                                },
                              });
                            }}
                            onCancelSession={(sessionId: string) => {
                              let session: Session | undefined;
                              let bundleUsage: typeof user.bundles_usage[0] | undefined;
                              
                              for (const bundle of user.bundles_usage) {
                                session = bundle.sessions?.find((s: Session) => s.id === sessionId);
                                if (session) {
                                  bundleUsage = bundle;
                                  break;
                                }
                              }
                              
                              if (session?.source_id && bundleUsage) {
                                setSessionToCancel({ session, bundleUsage });
                                setCancelDialogOpen(true);
                              }
                            }}
                            onCreateDoc={(sessionId: string) => {
                              createDocument(sessionId);
                            }}
                            onMarkNoShow={(sessionId: string) => {
                              let session: Session | undefined;
                              for (const bundle of user.bundles_usage) {
                                session = bundle.sessions?.find((s: Session) => s.id === sessionId);
                                if (session) break;
                              }
                              if (session?.source_id) {
                                if (session.is_no_show) {
                                  undoNoShow({ source_id: session.source_id });
                                } else {
                                  createNoShow({ source_id: session.source_id });
                                }
                              }
                            }}
                            onRescheduleSession={async (sessionId: string) => {
                              let session: Session | undefined;
                              let bundleStandardId = "";
                              for (const bundle of user.bundles_usage) {
                                session = bundle.sessions?.find((s: Session) => s.id === sessionId);
                                if (session) {
                                  bundleStandardId = bundle.bundle?.standard_id ?? "";
                                  break;
                                }
                              }
                              if (session?.source_id) {
                                try {
                                  const isWomens = isWomensHealthBundle(bundleStandardId);
                                  const baseUrl = isWomens ? DASHBOARD_BASE_URL : "https://rockethealth.app";
                                  const rescheduleLink = `${baseUrl}/reschedule/${session.source_id}`;
                                  navigator.clipboard.writeText(rescheduleLink);
                                  toast({
                                    title: "Copied",
                                    description: "Reschedule link copied to clipboard",
                                  });
                                } catch {
                                  toast({
                                    title: "Error",
                                    description: "Failed to copy reschedule link",
                                    variant: "destructive",
                                  });
                                }
                              } else {
                                toast({
                                  title: "Error",
                                  description: "Session source_id not available",
                                  variant: "destructive",
                                });
                              }
                            }}
                          />
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        }
      />
      <Dialog
        open={cancelDialogOpen}
        onOpenChange={(open) => {
          setCancelDialogOpen(open);
          if (!open) {
            setSelectedCancelReason("");
            setCancelReason(undefined);
            setSessionToCancel(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Cancel {sessionToCancel?.session?.invitee_full_name} Session
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Select cancellation reason
              </label>
              <Select
                value={selectedCancelReason}
                onValueChange={setSelectedCancelReason}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {CANCELLATION_REASONS.map((reason) => (
                    <SelectItem key={reason} value={reason}>
                      {reason}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedCancelReason === "Others (type reason manually if not listed)" && (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Please provide a reason for cancellation
                </label>
                <Textarea
                  placeholder="Type your reason here"
                  value={cancelReason ?? ""}
                  onChange={(e) => setCancelReason(e.target.value || undefined)}
                  className="min-h-[100px]"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCancelDialogOpen(false);
                setSelectedCancelReason("");
                setCancelReason(undefined);
                setSessionToCancel(null);
              }}
            >
              Close
            </Button>
            <Button
              variant="destructive"
              disabled={
                !selectedCancelReason ||
                (selectedCancelReason === "Others (type reason manually if not listed)" && !cancelReason)
              }
              onClick={() => {
                if (!sessionToCancel) return;
                const { session, bundleUsage } = sessionToCancel;
                const finalReason =
                  selectedCancelReason === "Others (type reason manually if not listed)"
                    ? cancelReason
                    : selectedCancelReason;
                const shouldDecrementUsage = session.status === "active" && !session.is_no_show;

                cancelSession(
                  { source_id: session.source_id, reason: finalReason },
                  {
                    onSuccess: () => {
                      if (shouldDecrementUsage) {
                        const currentUtilized = bundleUsage.utilized_session_quantity;
                        const newUtilized = Math.max(0, currentUtilized - 1);
                        const wasCompleted = bundleUsage.status === "COMPLETED";
                        const isExpired = new Date(bundleUsage.end_date) < new Date();

                        updateUtilized(
                          {
                            usage_id: bundleUsage.id,
                            new_utilization_session__count: newUtilized,
                          },
                          {
                            onSuccess: () => {
                              if (wasCompleted && newUtilized < bundleUsage.total_session_quantity) {
                                updateBundleStatus(
                                  {
                                    bundle_id: bundleUsage.id,
                                    status: isExpired ? "EXPIRED" : "ACTIVE",
                                  },
                                  {
                                    onSuccess: () => {
                                      toast({
                                        title: "Success",
                                        description: "Session cancelled, bundle usage and status updated",
                                      });
                                    },
                                    onError: () => {
                                      toast({
                                        title: "Success",
                                        description: "Session cancelled and bundle usage updated",
                                      });
                                    },
                                  }
                                );
                              } else {
                                toast({
                                  title: "Success",
                                  description: "Session cancelled and bundle usage updated",
                                });
                              }
                            },
                            onError: () => {
                              toast({
                                title: "Session cancelled",
                                description: "Bundle usage will update shortly",
                              });
                            },
                          }
                        );
                      }
                    },
                  }
                );

                setCancelDialogOpen(false);
                setSelectedCancelReason("");
                setCancelReason(undefined);
                setSessionToCancel(null);
              }}
            >
              Confirm Cancellation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <HasAccess permission="write">
        {user && (
          <>
            <VaulDrawer
              drawerDirection="right"
              isOpen={showBundleUsageFormDrawer}
              onOpenChange={setShowBundleUsageFormDrawer}
              triggerChildren={null}
              contentChildren={
                <AddEditBundleUsageForm
                  editMode={false}
                  onClose={() => setShowBundleUsageFormDrawer(false)}
                  initialData={null}
                  userId={user.id}
                />
              }
            />
            <VaulDrawer
              drawerDirection="right"
              isOpen={showUpdateUserDetailsDialog}
              onOpenChange={setShowUpdateUserDetailsDialog}
              triggerChildren={null}
              contentChildren={
                <UpdateUserDetailsDialog
                  user={user}
                  onClose={() => setShowUpdateUserDetailsDialog(false)}
                />
              }
            />
          </>
        )}
      </HasAccess>
    </>
    </AreaScope>
  );
}
